import React, { useEffect, useState } from "react";
import { AlertCircle, Clock, DollarSign, TrendingUp } from "lucide-react";
import BottomSheet from "../../../../../components/dashboards/components/shared/BottomSheet";
import NCAAService from "../../../../../components/dashboards/services/ncaaService";

const EMPTY_ODDS = "--";

const MobileNCAAFOddsSheet = ({ isOpen, game, onClose }) => {
  const [odds, setOdds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOdds = async () => {
      if (!isOpen || !game?.game?.id) return;
      try {
        setLoading(true);
        setError(null);
        const oddsData = await NCAAService.getOdds(game.game.id);
        setOdds(oddsData);
      } catch (err) {
        console.error("Failed to load NCAAF odds:", err);
        setError("Failed to load odds data");
      } finally {
        setLoading(false);
      }
    };

    fetchOdds();
  }, [game, isOpen]);

  const getTeamOdds = (side = "home") => {
    // Odds payload shape varies; fall back to placeholders
    const bookmaker = odds?.response?.[0];
    const market = bookmaker?.markets?.[0];
    const selection = market?.outcomes?.find((o) => o.name?.toLowerCase().includes(side));
    return {
      line: selection?.point ?? EMPTY_ODDS,
      price: selection?.price ?? EMPTY_ODDS
    };
  };

  const homeOdds = odds ? getTeamOdds("home") : { line: EMPTY_ODDS, price: EMPTY_ODDS };
  const awayOdds = odds ? getTeamOdds("away") : { line: EMPTY_ODDS, price: EMPTY_ODDS };

  const sections = [
    {
      title: "Point Spread",
      options: [
        { label: game?.teams?.away?.name || "Away", value: awayOdds.line, odds: awayOdds.price },
        { label: game?.teams?.home?.name || "Home", value: homeOdds.line, odds: homeOdds.price }
      ]
    },
    {
      title: "Money Line",
      options: [
        { label: game?.teams?.away?.name || "Away", value: EMPTY_ODDS, odds: EMPTY_ODDS },
        { label: game?.teams?.home?.name || "Home", value: EMPTY_ODDS, odds: EMPTY_ODDS }
      ]
    },
    {
      title: "Total Points",
      options: [
        { label: "Over", value: EMPTY_ODDS, odds: EMPTY_ODDS },
        { label: "Under", value: EMPTY_ODDS, odds: EMPTY_ODDS }
      ]
    }
  ];

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Betting Odds" maxHeight="85vh">
      {!game ? (
        <div className="p-6 text-center text-sm text-white/60">Select a game to view odds.</div>
      ) : (
        <div className="p-4 space-y-5">
          <div className="flex items-center justify-center gap-6">
            <TeamBlock team={game?.teams?.away} score={game?.scores?.away?.total} />
            <div className="text-white/60 text-sm font-semibold">@</div>
            <TeamBlock team={game?.teams?.home} score={game?.scores?.home?.total} />
          </div>

          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-400" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">
              <AlertCircle size={16} className="text-red-300" />
              <span>{error}</span>
            </div>
          )}

          {!loading &&
            !error &&
            sections.map((section) => (
              <div key={section.title} className="bg-white/[0.05] rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-white/80 mb-3">{section.title}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {section.options.map((option) => (
                    <div
                      key={`${section.title}-${option.label}`}
                      className="bg-white/[0.06] rounded-xl p-3 text-center border border-white/5"
                    >
                      <p className="text-xs text-white/60 mb-1">{option.label}</p>
                      <p className="text-lg font-bold text-emerald-300 tabular-nums">{option.value}</p>
                      <p className="text-xs text-white/50">{option.odds}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

          {!loading && !error && (
            <p className="text-xs text-white/50 text-center pt-2 flex items-center justify-center gap-1">
              <DollarSign size={12} /> Odds data may be limited; using latest available values.
            </p>
          )}
        </div>
      )}
    </BottomSheet>
  );
};

const TeamBlock = ({ team, score }) => (
  <div className="text-center">
    {team?.logo && (
      <img src={team.logo} alt={team.name} className="w-12 h-12 object-contain mx-auto mb-2" />
    )}
    <p className="text-sm font-semibold text-white">{team?.name || "Team"}</p>
    {score !== undefined && <p className="text-xl font-bold text-white mt-1 tabular-nums">{score}</p>}
  </div>
);

export default MobileNCAAFOddsSheet;
